import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import AuthService from '../../services/AuthService';
import PropertyService from '../../services/PropertyService';
import BuilderService from '../../services/BuilderService';
import {
  PROPERTY_STATUSES, PROPERTY_CATEGORIES, SIZE_UNITS, FACING_OPTIONS, COMMON_AMENITIES,
  INVENTORY_SOURCES,
} from '../../constants/propertyConstants';
import PropertyDocsMediaUpload from './PropertyDocsMediaUpload';

const EMPTY_FORM = {
  propertyName: '', ownerName: '', builderName: '', propertyCategory: 'Residential',
  propertyStatus: 'Available', projectName: '', towerBlock: '', floorNumber: '',
  unitNumber: '', streetAddress: '', city: '', state: '', country: 'India', postalCode: '',
  latitude: '', longitude: '', price: '', maintenanceCharge: '', size: '', sizeUnit: 'SqFt',
  description: '', propertyType: '', yearBuilt: '', bedrooms: '', bathrooms: '', facing: '',
  amenitiesJson: '[]', documentsJson: '[]', imagesJson: '[]',
  inventorySource: 'Own', builderId: '', channelCommissionPercent: '', channelAgreementNotes: '',
};

const fld = { display: 'flex', flexDirection: 'column', gap: 4 };
const lbl = { fontSize: 11, fontWeight: 700, color: '#8898aa', textTransform: 'uppercase' };
const inp = { padding: '10px 14px', borderRadius: 9, border: '1px solid #e3e6ec', fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' };
const section = (color) => ({ background: '#fff', borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,.06)', padding: 22, marginBottom: 18, borderTop: `3px solid ${color}` });

export default function AddProperty() {
  const toast    = useRef(null);
  const navigate = useNavigate();
  const clientId = AuthService.getLoggedInClientId();
  const [form,    setForm]    = useState({ ...EMPTY_FORM, clientId });
  const [saving,  setSaving]  = useState(false);
  const [amenities, setAmenities] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [images, setImages] = useState([]);
  const [builders, setBuilders] = useState([]);

  useEffect(() => {
    if (!clientId) return;
    BuilderService.getByClientId(clientId).then(setBuilders).catch(() => setBuilders([]));
  }, [clientId]);

  const set = (name, val) => setForm(f => ({ ...f, [name]: val }));
  const onChange = e => set(e.target.name, e.target.value);

  const toggleAmenity = (a) => {
    const next = amenities.includes(a) ? amenities.filter(x => x !== a) : [...amenities, a];
    setAmenities(next);
    set('amenitiesJson', JSON.stringify(next));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.propertyName) { toast.current?.show({ severity: 'warn', summary: 'Required', detail: 'Property name is required' }); return; }
    if (form.inventorySource === 'ChannelPartner' && !form.builderId) {
      toast.current?.show({ severity: 'warn', summary: 'Builder', detail: 'Select a linked builder for channel partner inventory.' });
      return;
    }
    setSaving(true);
    try {
      const src = form.inventorySource || 'Own';
      await PropertyService.createProperty({
        ...form,
        documentsJson: JSON.stringify(documents),
        imagesJson: JSON.stringify(images),
        price: Number(form.price) || 0,
        size:  Number(form.size)  || 0,
        floorNumber: form.floorNumber ? Number(form.floorNumber) : null,
        bedrooms:  Number(form.bedrooms)  || 0,
        bathrooms: Number(form.bathrooms) || 0,
        yearBuilt: Number(form.yearBuilt) || 0,
        latitude:  form.latitude  ? Number(form.latitude)  : null,
        longitude: form.longitude ? Number(form.longitude) : null,
        maintenanceCharge: form.maintenanceCharge ? Number(form.maintenanceCharge) : null,
        inventorySource: src,
        builderId: src === 'ChannelPartner' && form.builderId ? Number(form.builderId) : null,
        channelCommissionPercent:
          src === 'ChannelPartner' && form.channelCommissionPercent !== ''
            ? Number(form.channelCommissionPercent)
            : null,
        channelAgreementNotes: src === 'ChannelPartner' ? (form.channelAgreementNotes || null) : null,
      });
      toast.current?.show({ severity: 'success', summary: 'Created', detail: 'Property added!', life: 2000 });
      setTimeout(() => navigate('/propertylisting'), 1500);
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to save property' });
    }
    setSaving(false);
  };

  return (
    <div style={{ padding: 24, background: '#f4f7fa', minHeight: '100vh' }}>
      <Toast ref={toast} />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button onClick={() => navigate('/propertylisting')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5e72e4', fontWeight: 700, fontSize: 14, padding: 0 }}>
          <i className="fas fa-arrow-left" style={{ marginRight: 6 }} />Back
        </button>
        <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#1a1f36' }}>
          <i className="fas fa-plus-circle" style={{ color: '#5e72e4', marginRight: 10 }} />Add Property
        </h3>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Section 1 — Basic */}
        <div style={section('#5e72e4')}>
          <h6 style={{ margin: '0 0 16px', color: '#5e72e4', fontWeight: 700 }}><i className="fas fa-home" style={{ marginRight: 7 }} />Basic Information</h6>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 14 }}>
            <div style={fld}><label style={lbl}>Property Name *</label><input name="propertyName" value={form.propertyName} onChange={onChange} style={inp} required /></div>
            <div style={fld}><label style={lbl}>Owner Name</label><input name="ownerName" value={form.ownerName} onChange={onChange} style={inp} /></div>
            <div style={fld}><label style={lbl}>Builder Name</label><input name="builderName" value={form.builderName} onChange={onChange} style={inp} /></div>
            <div style={fld}>
              <label style={lbl}>Category</label>
              <select name="propertyCategory" value={form.propertyCategory} onChange={onChange} style={inp}>
                {PROPERTY_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div style={fld}>
              <label style={lbl}>Status</label>
              <select name="propertyStatus" value={form.propertyStatus} onChange={onChange} style={inp}>
                {PROPERTY_STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div style={fld}><label style={lbl}>Property Type (e.g. 2BHK, Villa)</label><input name="propertyType" value={form.propertyType} onChange={onChange} style={inp} /></div>
          </div>
        </div>

        {/* Inventory ownership */}
        <div style={section('#11cdef')}>
          <h6 style={{ margin: '0 0 16px', color: '#11cdef', fontWeight: 700 }}>
            <i className="fas fa-handshake" style={{ marginRight: 7 }} />Inventory ownership
          </h6>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 14 }}>
            <div style={fld}>
              <label style={lbl}>Source</label>
              <select
                style={inp}
                value={form.inventorySource || 'Own'}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((f) => ({
                    ...f,
                    inventorySource: v,
                    ...(v === 'Own'
                      ? { builderId: '', channelCommissionPercent: '', channelAgreementNotes: '' }
                      : {}),
                  }));
                }}
              >
                {INVENTORY_SOURCES.map((x) => (
                  <option key={x.id} value={x.id}>{x.label}</option>
                ))}
              </select>
            </div>
            {form.inventorySource === 'ChannelPartner' && (
              <>
                <div style={fld}>
                  <label style={lbl}>Linked builder *</label>
                  <select style={inp} value={form.builderId} onChange={(e) => set('builderId', e.target.value)}>
                    <option value="">— Select builder —</option>
                    {builders.map((b) => (
                      <option key={b.id} value={b.id}>{b.companyName || `Builder #${b.id}`}</option>
                    ))}
                  </select>
                </div>
                <div style={fld}>
                  <label style={lbl}>Commission % (optional)</label>
                  <input
                    type="number"
                    step="0.01"
                    style={inp}
                    value={form.channelCommissionPercent}
                    onChange={(e) => set('channelCommissionPercent', e.target.value)}
                    placeholder="Uses builder default if empty"
                  />
                </div>
                <div style={{ ...fld, gridColumn: '1 / -1' }}>
                  <label style={lbl}>Agreement notes</label>
                  <textarea
                    rows={2}
                    style={{ ...inp, resize: 'vertical' }}
                    value={form.channelAgreementNotes}
                    onChange={(e) => set('channelAgreementNotes', e.target.value)}
                    placeholder="Commission terms, validity, reference to agreement…"
                  />
                </div>
              </>
            )}
          </div>
          {form.inventorySource === 'ChannelPartner' && builders.length === 0 && (
            <p style={{ margin: '12px 0 0', fontSize: 12, color: '#fb6340' }}>
              Add at least one builder record for this agency before saving channel inventory.
            </p>
          )}
        </div>

        {/* Section 2 — Project Hierarchy */}
        <div style={section('#825ee4')}>
          <h6 style={{ margin: '0 0 16px', color: '#825ee4', fontWeight: 700 }}><i className="fas fa-city" style={{ marginRight: 7 }} />Project Hierarchy (optional)</h6>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 14 }}>
            <div style={fld}><label style={lbl}>Project Name</label><input name="projectName" value={form.projectName} onChange={onChange} style={inp} /></div>
            <div style={fld}><label style={lbl}>Tower / Block</label><input name="towerBlock" value={form.towerBlock} onChange={onChange} style={inp} /></div>
            <div style={fld}><label style={lbl}>Floor Number</label><input type="number" name="floorNumber" value={form.floorNumber} onChange={onChange} style={inp} min="0" /></div>
            <div style={fld}><label style={lbl}>Unit Number</label><input name="unitNumber" value={form.unitNumber} onChange={onChange} style={inp} /></div>
          </div>
        </div>

        {/* Section 3 — Location */}
        <div style={section('#11cdef')}>
          <h6 style={{ margin: '0 0 16px', color: '#11cdef', fontWeight: 700 }}><i className="fas fa-map-marker-alt" style={{ marginRight: 7 }} />Location</h6>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 14 }}>
            <div style={{ ...fld, gridColumn: 'span 2' }}><label style={lbl}>Street Address</label><input name="streetAddress" value={form.streetAddress} onChange={onChange} style={inp} /></div>
            <div style={fld}><label style={lbl}>City</label><input name="city" value={form.city} onChange={onChange} style={inp} /></div>
            <div style={fld}><label style={lbl}>State</label><input name="state" value={form.state} onChange={onChange} style={inp} /></div>
            <div style={fld}><label style={lbl}>Country</label><input name="country" value={form.country} onChange={onChange} style={inp} /></div>
            <div style={fld}><label style={lbl}>Postal Code</label><input name="postalCode" value={form.postalCode} onChange={onChange} style={inp} /></div>
            <div style={fld}><label style={lbl}>Latitude (GPS)</label><input type="number" step="any" name="latitude" value={form.latitude} onChange={onChange} style={inp} /></div>
            <div style={fld}><label style={lbl}>Longitude (GPS)</label><input type="number" step="any" name="longitude" value={form.longitude} onChange={onChange} style={inp} /></div>
          </div>
        </div>

        {/* Section 4 — Pricing & Size */}
        <div style={section('#2dce89')}>
          <h6 style={{ margin: '0 0 16px', color: '#2dce89', fontWeight: 700 }}><i className="fas fa-rupee-sign" style={{ marginRight: 7 }} />Pricing & Size</h6>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 14 }}>
            <div style={fld}><label style={lbl}>Price (₹) *</label><input type="number" name="price" value={form.price} onChange={onChange} style={inp} min="0" /></div>
            <div style={fld}><label style={lbl}>Maintenance Charge/mo</label><input type="number" name="maintenanceCharge" value={form.maintenanceCharge} onChange={onChange} style={inp} min="0" /></div>
            <div style={fld}><label style={lbl}>Size</label><input type="number" name="size" value={form.size} onChange={onChange} style={inp} min="0" /></div>
            <div style={fld}>
              <label style={lbl}>Size Unit</label>
              <select name="sizeUnit" value={form.sizeUnit} onChange={onChange} style={inp}>
                {SIZE_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div style={fld}><label style={lbl}>Bedrooms</label><input type="number" name="bedrooms" value={form.bedrooms} onChange={onChange} style={inp} min="0" /></div>
            <div style={fld}><label style={lbl}>Bathrooms</label><input type="number" name="bathrooms" value={form.bathrooms} onChange={onChange} style={inp} min="0" /></div>
            <div style={fld}><label style={lbl}>Year Built</label><input type="number" name="yearBuilt" value={form.yearBuilt} onChange={onChange} style={inp} min="1900" max="2100" /></div>
            <div style={fld}>
              <label style={lbl}>Facing</label>
              <select name="facing" value={form.facing} onChange={onChange} style={inp}>
                <option value="">Select</option>
                {FACING_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Section 5 — Amenities */}
        <div style={section('#fb6340')}>
          <h6 style={{ margin: '0 0 14px', color: '#fb6340', fontWeight: 700 }}><i className="fas fa-star" style={{ marginRight: 7 }} />Amenities</h6>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {COMMON_AMENITIES.map(a => (
              <button type="button" key={a} onClick={() => toggleAmenity(a)}
                style={{ padding: '6px 14px', borderRadius: 20, border: `2px solid ${amenities.includes(a) ? '#2dce89' : '#e3e6ec'}`, background: amenities.includes(a) ? '#2dce8918' : '#fff', color: amenities.includes(a) ? '#2dce89' : '#8898aa', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                {amenities.includes(a) && <i className="fas fa-check" style={{ marginRight: 5 }} />}{a}
              </button>
            ))}
          </div>
        </div>

        {/* Section 6 — Docs & Media */}
        <div style={section('#344767')}>
          <h6 style={{ margin: '0 0 8px', color: '#344767', fontWeight: 700 }}>
            <i className="fas fa-folder-open" style={{ marginRight: 7 }} />Docs &amp; Media
          </h6>
          <p style={{ margin: '0 0 18px', fontSize: 13, color: '#8898aa' }}>
            Upload sale deeds, NOCs, layout plans, brochures, and photos. Files are stored with the property record (base64 for uploads, or external links).
          </p>
          <PropertyDocsMediaUpload
            documents={documents}
            images={images}
            onDocumentsChange={setDocuments}
            onImagesChange={setImages}
            onToast={(opts) => toast.current?.show({ ...opts, life: 2800 })}
          />
        </div>

        {/* Section 7 — Description */}
        <div style={section('#8898aa')}>
          <h6 style={{ margin: '0 0 12px', color: '#8898aa', fontWeight: 700 }}><i className="fas fa-align-left" style={{ marginRight: 7 }} />Description</h6>
          <textarea name="description" value={form.description} onChange={onChange} rows={4}
            placeholder="Enter property description…"
            style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" onClick={() => navigate('/propertylisting')}
            style={{ padding: '12px 28px', borderRadius: 10, border: '1px solid #e3e6ec', background: '#fff', color: '#8898aa', fontWeight: 700, cursor: 'pointer' }}>
            Cancel
          </button>
          <button type="submit" disabled={saving}
            style={{ padding: '12px 32px', background: 'linear-gradient(135deg,#5e72e4,#825ee4)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 15 }}>
            {saving ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }} />Saving…</> : <><i className="fas fa-save" style={{ marginRight: 8 }} />Save Property</>}
          </button>
        </div>
      </form>
    </div>
  );
}
